import { makeAutoObservable } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import {
  ProductionCost,
  LaborEntry,
  EquipmentCost,
  CostVariance,
  StandardCost,
  ProductionCostCategory,
  LaborType,
  EquipmentCostType,
  CostVarianceType,
  ProductionCostSummary,
} from "../types";

export interface ProductionCostFormData {
  id?: string;
  productionOrderId: string;
  jobId?: string;
  category: ProductionCostCategory;
  description: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  standardCost: number;
  costDate: string;
  referenceId?: string;
  notes?: string;
}

export interface LaborEntryFormData {
  id?: string;
  employeeId: string;
  productionOrderId?: string;
  jobId?: string;
  laborType: LaborType;
  workDate: string;
  startTime: string;
  endTime: string;
  hourlyRate: number;
  overtimeRate: number;
  department?: string;
  workCenter?: string;
  operation?: string;
  notes?: string;
}

export interface EquipmentCostFormData {
  id?: string;
  equipmentId: string;
  productionOrderId?: string;
  jobId?: string;
  costType: EquipmentCostType;
  usageDate: string;
  hoursUsed: number;
  costPerHour: number;
  standardCost?: number;
  maintenanceCategory?: string;
  operatorId?: string;
  notes?: string;
}

class ProductionCostsStore {
  rootStore: RootStore;

  // Observables
  selectedProductionOrderId: string | null = null;
  selectedJobId: string | null = null;
  productionCostFormData: ProductionCostFormData;
  laborEntryFormData: LaborEntryFormData;
  equipmentCostFormData: EquipmentCostFormData;
  isEditMode = false;
  filterCategory: "all" | ProductionCostCategory = "all";
  filterPeriod = "";
  searchQuery = "";
  dateRange = {
    start: "",
    end: "",
  };
  selectedTabIndex = 0; // 0: Production Costs, 1: Labor, 2: Equipment, 3: Variances

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.productionCostFormData = this.getInitialProductionCostFormData();
    this.laborEntryFormData = this.getInitialLaborEntryFormData();
    this.equipmentCostFormData = this.getInitialEquipmentCostFormData();

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setSelectedTabIndex = (index: number) => {
    this.selectedTabIndex = index;
  };

  setFilterCategory = (category: "all" | ProductionCostCategory) => {
    this.filterCategory = category;
  };

  setFilterPeriod = (period: string) => {
    this.filterPeriod = period;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRange = { start, end };
  };

  selectProductionOrder = (orderId: string | null) => {
    this.selectedProductionOrderId = orderId;
  };

  selectJob = (jobId: string | null) => {
    this.selectedJobId = jobId;
  };

  // Production Cost Form Actions
  openCreateProductionCostForm = (productionOrderId?: string, jobId?: string) => {
    this.productionCostFormData = {
      ...this.getInitialProductionCostFormData(),
      productionOrderId: productionOrderId || "",
      jobId,
    };
    this.isEditMode = false;
  };

  openEditProductionCostForm = (costId: string) => {
    const cost = this.productionCosts.find((c) => c.id === costId);
    if (!cost) return;

    this.productionCostFormData = {
      id: cost.id,
      productionOrderId: cost.productionOrderId,
      jobId: cost.jobId,
      category: cost.category,
      description: cost.description,
      quantity: cost.quantity,
      unitCost: cost.unitCost,
      totalCost: cost.totalCost,
      standardCost: cost.standardCost,
      costDate: cost.costDate,
      referenceId: cost.referenceId,
      notes: cost.notes,
    };
    this.isEditMode = true;
  };

  updateProductionCostFormData = (
    field: keyof ProductionCostFormData,
    value: any
  ) => {
    // Auto-calculate total cost and variance when quantity or unit cost changes
    if (field === "quantity" || field === "unitCost" || field === "standardCost") {
      const quantity =
        field === "quantity" ? value : this.productionCostFormData.quantity;
      const unitCost =
        field === "unitCost" ? value : this.productionCostFormData.unitCost;
      const totalCost = quantity * unitCost;

      this.productionCostFormData = {
        ...this.productionCostFormData,
        [field]: value,
        totalCost,
      };
    } else {
      this.productionCostFormData = {
        ...this.productionCostFormData,
        [field]: value,
      };
    }
  };

  saveProductionCost = async () => {
    try {
      const { totalCost, standardCost } = this.productionCostFormData;
      const variance = totalCost - standardCost;
      const variancePercent = standardCost > 0 ? (variance / standardCost) * 100 : 0;
      const varianceType: CostVarianceType =
        variance < 0
          ? CostVarianceType.FAVORABLE
          : variance > 0
          ? CostVarianceType.UNFAVORABLE
          : CostVarianceType.NEUTRAL;

      const costData = {
        ...this.productionCostFormData,
        variance,
        variancePercent,
        varianceType,
      };

      if (this.isEditMode && this.productionCostFormData.id) {
        await this.productionCostsStore.patch(
          Number(this.productionCostFormData.id),
          costData
        );
      } else {
        await this.productionCostsStore.create(costData);
      }

      this.productionCostFormData = this.getInitialProductionCostFormData();
      this.isEditMode = false;
    } catch (error) {
      console.error("Failed to save production cost:", error);
      throw error;
    }
  };

  deleteProductionCost = async (costId: string) => {
    try {
      await this.productionCostsStore.remove(Number(costId));
    } catch (error) {
      console.error("Failed to delete production cost:", error);
      throw error;
    }
  };

  // Labor Entry Form Actions
  openCreateLaborEntryForm = (productionOrderId?: string, jobId?: string) => {
    this.laborEntryFormData = {
      ...this.getInitialLaborEntryFormData(),
      productionOrderId,
      jobId,
    };
    this.isEditMode = false;
  };

  updateLaborEntryFormData = (field: keyof LaborEntryFormData, value: any) => {
    // Auto-calculate hours worked when start/end time changes
    if (field === "startTime" || field === "endTime") {
      const startTime =
        field === "startTime" ? value : this.laborEntryFormData.startTime;
      const endTime =
        field === "endTime" ? value : this.laborEntryFormData.endTime;

      if (startTime && endTime) {
        const start = new Date(`2000-01-01T${startTime}`);
        const end = new Date(`2000-01-01T${endTime}`);
        const hoursWorked = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

        this.laborEntryFormData = {
          ...this.laborEntryFormData,
          [field]: value,
        };
      } else {
        this.laborEntryFormData = {
          ...this.laborEntryFormData,
          [field]: value,
        };
      }
    } else {
      this.laborEntryFormData = {
        ...this.laborEntryFormData,
        [field]: value,
      };
    }
  };

  saveLaborEntry = async () => {
    try {
      const { startTime, endTime, hourlyRate, overtimeRate, laborType } =
        this.laborEntryFormData;

      // Calculate hours
      const start = new Date(`2000-01-01T${startTime}`);
      const end = new Date(`2000-01-01T${endTime}`);
      const hoursWorked = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

      // Calculate regular and overtime hours (assuming 8 hours is standard)
      const regularHours = Math.min(hoursWorked, 8);
      const overtimeHours = Math.max(hoursWorked - 8, 0);

      // Calculate total labor cost
      const totalLabor =
        regularHours * hourlyRate + overtimeHours * overtimeRate;

      const laborData = {
        ...this.laborEntryFormData,
        hoursWorked,
        regularHours,
        overtimeHours,
        totalLabor,
      };

      await this.laborEntriesStore.create(laborData);

      this.laborEntryFormData = this.getInitialLaborEntryFormData();
    } catch (error) {
      console.error("Failed to save labor entry:", error);
      throw error;
    }
  };

  deleteLaborEntry = async (entryId: string) => {
    try {
      await this.laborEntriesStore.remove(Number(entryId));
    } catch (error) {
      console.error("Failed to delete labor entry:", error);
      throw error;
    }
  };

  // Equipment Cost Form Actions
  openCreateEquipmentCostForm = (productionOrderId?: string, jobId?: string) => {
    this.equipmentCostFormData = {
      ...this.getInitialEquipmentCostFormData(),
      productionOrderId,
      jobId,
    };
    this.isEditMode = false;
  };

  updateEquipmentCostFormData = (
    field: keyof EquipmentCostFormData,
    value: any
  ) => {
    // Auto-calculate total cost when hours or cost per hour changes
    if (field === "hoursUsed" || field === "costPerHour") {
      const hoursUsed =
        field === "hoursUsed" ? value : this.equipmentCostFormData.hoursUsed;
      const costPerHour =
        field === "costPerHour" ? value : this.equipmentCostFormData.costPerHour;
      const totalCost = hoursUsed * costPerHour;

      this.equipmentCostFormData = {
        ...this.equipmentCostFormData,
        [field]: value,
      };
    } else {
      this.equipmentCostFormData = {
        ...this.equipmentCostFormData,
        [field]: value,
      };
    }
  };

  saveEquipmentCost = async () => {
    try {
      const { hoursUsed, costPerHour, standardCost } =
        this.equipmentCostFormData;
      const totalCost = hoursUsed * costPerHour;
      const variance = standardCost ? totalCost - standardCost : undefined;
      const varianceType = variance
        ? variance < 0
          ? CostVarianceType.FAVORABLE
          : variance > 0
          ? CostVarianceType.UNFAVORABLE
          : CostVarianceType.NEUTRAL
        : undefined;

      const equipmentData = {
        ...this.equipmentCostFormData,
        totalCost,
        variance,
        varianceType,
      };

      await this.equipmentCostsStore.create(equipmentData);

      this.equipmentCostFormData = this.getInitialEquipmentCostFormData();
    } catch (error) {
      console.error("Failed to save equipment cost:", error);
      throw error;
    }
  };

  deleteEquipmentCost = async (costId: string) => {
    try {
      await this.equipmentCostsStore.remove(Number(costId));
    } catch (error) {
      console.error("Failed to delete equipment cost:", error);
      throw error;
    }
  };

  // Load Data Actions
  loadProductionCosts = async () => {
    try {
      await this.productionCostsStore.find({});
    } catch (error) {
      console.error("Failed to load production costs:", error);
      throw error;
    }
  };

  loadLaborEntries = async () => {
    try {
      await this.laborEntriesStore.find({});
    } catch (error) {
      console.error("Failed to load labor entries:", error);
      throw error;
    }
  };

  loadEquipmentCosts = async () => {
    try {
      await this.equipmentCostsStore.find({});
    } catch (error) {
      console.error("Failed to load equipment costs:", error);
      throw error;
    }
  };

  loadCostVariances = async () => {
    try {
      await this.costVariancesStore.find({});
    } catch (error) {
      console.error("Failed to load cost variances:", error);
      throw error;
    }
  };

  loadStandardCosts = async () => {
    try {
      await this.standardCostsStore.find({});
    } catch (error) {
      console.error("Failed to load standard costs:", error);
      throw error;
    }
  };

  // Initial Form Data Methods
  private getInitialProductionCostFormData(): ProductionCostFormData {
    return {
      productionOrderId: "",
      category: ProductionCostCategory.DIRECT_MATERIAL,
      description: "",
      quantity: 1,
      unitCost: 0,
      totalCost: 0,
      standardCost: 0,
      costDate: new Date().toISOString().split("T")[0],
    };
  }

  private getInitialLaborEntryFormData(): LaborEntryFormData {
    return {
      employeeId: "",
      laborType: LaborType.DIRECT,
      workDate: new Date().toISOString().split("T")[0],
      startTime: "08:00",
      endTime: "17:00",
      hourlyRate: 0,
      overtimeRate: 0,
    };
  }

  private getInitialEquipmentCostFormData(): EquipmentCostFormData {
    return {
      equipmentId: "",
      costType: EquipmentCostType.OPERATION,
      usageDate: new Date().toISOString().split("T")[0],
      hoursUsed: 0,
      costPerHour: 0,
    };
  }

  // Computed values - Service Stores
  get productionCostsStore() {
    return this.rootStore.productionCostsStore as ServiceStore;
  }

  get laborEntriesStore() {
    return this.rootStore.laborEntriesStore as ServiceStore;
  }

  get equipmentCostsStore() {
    return this.rootStore.equipmentCostsStore as ServiceStore;
  }

  get costVariancesStore() {
    return this.rootStore.costVariancesStore as ServiceStore;
  }

  get standardCostsStore() {
    return this.rootStore.standardCostsStore as ServiceStore;
  }

  // Computed values - Entities
  get productionCosts(): ProductionCost[] {
    return (
      (this.productionCostsStore.entities as unknown as ProductionCost[]) || []
    );
  }

  get laborEntries(): LaborEntry[] {
    return (this.laborEntriesStore.entities as unknown as LaborEntry[]) || [];
  }

  get equipmentCosts(): EquipmentCost[] {
    return (
      (this.equipmentCostsStore.entities as unknown as EquipmentCost[]) || []
    );
  }

  get costVariances(): CostVariance[] {
    return (
      (this.costVariancesStore.entities as unknown as CostVariance[]) || []
    );
  }

  get standardCosts(): StandardCost[] {
    return (this.standardCostsStore.entities as unknown as StandardCost[]) || [];
  }

  get isLoading(): boolean {
    return (
      this.productionCostsStore.isServicePending ||
      this.laborEntriesStore.isServicePending ||
      this.equipmentCostsStore.isServicePending ||
      this.costVariancesStore.isServicePending ||
      this.standardCostsStore.isServicePending
    );
  }

  // Filtered Production Costs
  get filteredProductionCosts(): ProductionCost[] {
    let filtered = this.productionCosts;

    // Filter by category
    if (this.filterCategory !== "all") {
      filtered = filtered.filter((cost) => cost.category === this.filterCategory);
    }

    // Filter by production order
    if (this.selectedProductionOrderId) {
      filtered = filtered.filter(
        (cost) => cost.productionOrderId === this.selectedProductionOrderId
      );
    }

    // Filter by job
    if (this.selectedJobId) {
      filtered = filtered.filter((cost) => cost.jobId === this.selectedJobId);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (cost) =>
          cost.description.toLowerCase().includes(query) ||
          cost.productionOrderId.toLowerCase().includes(query)
      );
    }

    // Filter by date range
    if (this.dateRange.start) {
      filtered = filtered.filter((cost) => cost.costDate >= this.dateRange.start);
    }

    if (this.dateRange.end) {
      filtered = filtered.filter((cost) => cost.costDate <= this.dateRange.end);
    }

    return filtered;
  }

  // Variance Analysis Methods
  calculateVarianceForProductionOrder = (
    productionOrderId: string
  ): ProductionCostSummary => {
    const costs = this.productionCosts.filter(
      (c) => c.productionOrderId === productionOrderId
    );
    const labor = this.laborEntries.filter(
      (l) => l.productionOrderId === productionOrderId
    );
    const equipment = this.equipmentCosts.filter(
      (e) => e.productionOrderId === productionOrderId
    );

    const totalDirectMaterial = costs
      .filter((c) => c.category === ProductionCostCategory.DIRECT_MATERIAL)
      .reduce((sum, c) => sum + c.totalCost, 0);

    const totalDirectLabor = labor.reduce((sum, l) => sum + l.totalLabor, 0);

    const totalManufacturingOverhead = costs
      .filter((c) => c.category === ProductionCostCategory.MANUFACTURING_OVERHEAD)
      .reduce((sum, c) => sum + c.totalCost, 0);

    const totalProductionCost =
      totalDirectMaterial + totalDirectLabor + totalManufacturingOverhead;

    const totalStandardCost = costs.reduce((sum, c) => sum + c.standardCost, 0);

    const totalVariance = totalProductionCost - totalStandardCost;
    const variancePercent =
      totalStandardCost > 0 ? (totalVariance / totalStandardCost) * 100 : 0;

    const materialVariance = costs
      .filter((c) => c.category === ProductionCostCategory.DIRECT_MATERIAL)
      .reduce((sum, c) => sum + c.variance, 0);

    const laborVariance = labor.reduce(
      (sum, l) =>
        sum + (l.varianceHours ? l.varianceHours * l.hourlyRate : 0),
      0
    );

    const overheadVariance = costs
      .filter((c) => c.category === ProductionCostCategory.MANUFACTURING_OVERHEAD)
      .reduce((sum, c) => sum + c.variance, 0);

    const laborHours = labor.reduce((sum, l) => sum + l.hoursWorked, 0);
    const equipmentHours = equipment.reduce((sum, e) => sum + e.hoursUsed, 0);

    return {
      productionOrderId,
      period: new Date().toISOString().substring(0, 7), // YYYY-MM
      totalDirectMaterial,
      totalDirectLabor,
      totalManufacturingOverhead,
      totalProductionCost,
      totalStandardCost,
      totalVariance,
      variancePercent,
      unitCount: 0, // To be filled from production order data
      costPerUnit: 0, // To be calculated
      standardCostPerUnit: 0, // To be calculated
      laborHours,
      equipmentHours,
      materialVariance,
      laborVariance,
      overheadVariance,
    };
  };

  // Summary Statistics
  get productionCostSummary() {
    const totalCosts = this.productionCosts.reduce(
      (sum, c) => sum + c.totalCost,
      0
    );
    const totalStandardCosts = this.productionCosts.reduce(
      (sum, c) => sum + c.standardCost,
      0
    );
    const totalVariance = totalCosts - totalStandardCosts;

    const favorableVariances = this.productionCosts.filter(
      (c) => c.varianceType === CostVarianceType.FAVORABLE
    ).length;
    const unfavorableVariances = this.productionCosts.filter(
      (c) => c.varianceType === CostVarianceType.UNFAVORABLE
    ).length;

    return {
      totalCosts,
      totalStandardCosts,
      totalVariance,
      variancePercent:
        totalStandardCosts > 0 ? (totalVariance / totalStandardCosts) * 100 : 0,
      favorableVariances,
      unfavorableVariances,
      totalEntries: this.productionCosts.length,
    };
  }

  get laborSummary() {
    const totalHours = this.laborEntries.reduce(
      (sum, l) => sum + l.hoursWorked,
      0
    );
    const totalRegularHours = this.laborEntries.reduce(
      (sum, l) => sum + l.regularHours,
      0
    );
    const totalOvertimeHours = this.laborEntries.reduce(
      (sum, l) => sum + l.overtimeHours,
      0
    );
    const totalLaborCost = this.laborEntries.reduce(
      (sum, l) => sum + l.totalLabor,
      0
    );

    return {
      totalHours,
      totalRegularHours,
      totalOvertimeHours,
      totalLaborCost,
      totalEntries: this.laborEntries.length,
      averageHourlyRate:
        this.laborEntries.length > 0
          ? totalLaborCost / totalHours
          : 0,
    };
  }

  get equipmentSummary() {
    const totalHours = this.equipmentCosts.reduce(
      (sum, e) => sum + e.hoursUsed,
      0
    );
    const totalCost = this.equipmentCosts.reduce(
      (sum, e) => sum + e.totalCost,
      0
    );

    return {
      totalHours,
      totalCost,
      totalEntries: this.equipmentCosts.length,
      averageCostPerHour:
        totalHours > 0 ? totalCost / totalHours : 0,
    };
  }

  // Cost breakdown by category
  get costBreakdownByCategory() {
    const categories = Object.values(ProductionCostCategory);
    return categories.map((category) => {
      const total = this.productionCosts
        .filter((c) => c.category === category)
        .reduce((sum, c) => sum + c.totalCost, 0);
      const standard = this.productionCosts
        .filter((c) => c.category === category)
        .reduce((sum, c) => sum + c.standardCost, 0);
      const variance = total - standard;

      return {
        category,
        total,
        standard,
        variance,
        variancePercent: standard > 0 ? (variance / standard) * 100 : 0,
      };
    });
  }
}

export default ProductionCostsStore;
